<script setup lang="ts">
/**
 * Navigation : liste de texte, un point gris devant chaque entrée, point lime
 * avec halo sur l'entrée active (DESIGN.md). Sur mobile, la même liste devient
 * une barre en bas, les points au-dessus des libellés.
 *
 * Pas d'icônes : la barre doit rester lisible d'un coup d'œil, et une icône de
 * plus serait une chose de plus à interpréter au milieu d'un couloir.
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAuthStore } from '../stores/auth';
import { useHospitalStore } from '../stores/hospital';

const props = withDefaults(defineProps<{ variant?: 'side' | 'bottom' }>(), {
  variant: 'side',
});

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const hospital = useHospitalStore();

const entries = computed(() => {
  const membership = auth.membershipFor(hospital.currentId);
  const list = [
    { name: 'accueil', label: 'Accueil' },
    { name: 'salles', label: 'Salles' },
    { name: 'registre', label: 'Registre' },
  ];

  if (membership?.canAdminHospital === true || auth.user?.isGlobalAdmin === true) {
    list.push({ name: 'administration', label: 'Réglages' });
  }
  if (auth.user?.isGlobalAdmin === true) {
    list.push({ name: 'administration-plateforme', label: 'Plateforme' });
  }
  return list;
});

/**
 * Les deux écrans d'affichage sont lisibles par n'importe quel compte rattaché
 * à l'établissement : ce sont les mêmes chiffres, sans identité pour l'un,
 * internes pour l'autre. Seule l'adresse manquait.
 *
 * Ils s'ouvrent dans un onglet séparé, parce qu'ils occupent tout l'écran et
 * n'ont pas de menu : y aller dans l'onglet courant ferait perdre la page en
 * cours sans offrir de retour évident. Ils restent hors de la barre du bas —
 * un écran de couloir ne se pilote pas depuis un téléphone.
 */
const ecrans = [
  { name: 'ecran-attente', label: 'Écran salle d’attente' },
  { name: 'ecran-soignants', label: 'Écran soignants' },
];

function adresse(name: string): string {
  return router.resolve({ name }).href;
}

function isActive(name: string): boolean {
  return route.name === name;
}
</script>

<template>
  <nav :class="props.variant === 'bottom' ? 'flex justify-around' : 'flex flex-col gap-4'">
    <RouterLink
      v-for="entry in entries"
      :key="entry.name"
      :to="{ name: entry.name }"
      class="group flex items-center gap-3 text-body font-light text-ink-2 transition-colors hover:text-ink"
      :class="[
        props.variant === 'bottom' ? 'flex-col gap-1 px-3 py-1 text-label' : '',
        isActive(entry.name) ? 'text-ink' : '',
      ]"
    >
      <span
        class="size-1.5 shrink-0 rounded-full transition-all"
        :style="
          isActive(entry.name)
            ? 'background: var(--color-lime); box-shadow: var(--halo-lime)'
            : 'background: var(--color-ink-3)'
        "
      />
      {{ entry.label }}
    </RouterLink>

    <div v-if="props.variant === 'side'" class="mt-8 flex flex-col gap-3">
      <p class="text-meta text-ink-3">Affichage</p>
      <a
        v-for="ecran in ecrans"
        :key="ecran.name"
        class="text-label font-light text-ink-2 underline-offset-2 transition-colors hover:text-ink hover:underline"
        :href="adresse(ecran.name)"
        rel="noopener"
        target="_blank"
      >
        {{ ecran.label }}
      </a>
    </div>
  </nav>
</template>
